/**
 * Call Centre Phone Numbers Management Tab
 *
 * Shows Twilio phone numbers alongside their database mapping status.
 * Allows mapping Twilio numbers to brands for call routing.
 */

import React, { useState, useEffect, useCallback } from 'react';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Stack,
  Switch,
  FormControlLabel,
  Divider,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Phone as PhoneIcon,
  Settings as SettingsIcon,
  CloudDone as CloudDoneIcon,
  CloudOff as CloudOffIcon
} from '@mui/icons-material';
import callCentreService from '../../services/callCentreService';

const CallCentrePhoneNumbersTab = () => {
  const { confirmDialogProps, confirm } = useConfirmDialog();
  // State
  const [loading, setLoading] = useState(true);
  const [twilioNumbers, setTwilioNumbers] = useState([]);
  const [dbPhoneNumbers, setDbPhoneNumbers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [twilioStatus, setTwilioStatus] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog states
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState(null);

  // Form states
  const [mapForm, setMapForm] = useState({
    brandId: '',
    friendlyName: '',
    countryCode: '',
    ringTimeout: 30,
    voicemailEnabled: true,
    callbackEnabled: true
  });

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [twilioRes, dbRes, brandsRes, statusRes] = await Promise.allSettled([
        callCentreService.getTwilioNumbers(),
        callCentreService.getPhoneNumbers(),
        callCentreService.getCompanyBrands(),
        callCentreService.getTwilioStatus()
      ]);

      if (twilioRes.status === 'fulfilled' && twilioRes.value.success) {
        setTwilioNumbers(twilioRes.value.numbers || []);
      } else if (twilioRes.status === 'rejected') {
        console.error('Failed to load Twilio numbers:', twilioRes.reason);
      }

      if (dbRes.status === 'fulfilled' && dbRes.value.success) {
        setDbPhoneNumbers(dbRes.value.phoneNumbers || []);
      }

      if (brandsRes.status === 'fulfilled' && brandsRes.value.success) {
        setBrands(brandsRes.value.brands || []);
      }

      if (statusRes.status === 'fulfilled' && statusRes.value.success) {
        setTwilioStatus(statusRes.value);
      }

    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load phone numbers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Merge Twilio numbers with DB mappings
  const getMergedNumbers = () => {
    const dbMap = new Map();
    dbPhoneNumbers.forEach(db => {
      // Normalize phone number for matching
      const normalized = db.phone_number.replace(/\D/g, '');
      dbMap.set(normalized, db);
    });

    return twilioNumbers.map(twilio => {
      const normalized = twilio.phoneNumber.replace(/\D/g, '');
      const dbRecord = dbMap.get(normalized);
      return {
        ...twilio,
        dbRecord,
        isMapped: !!dbRecord,
        brandName: dbRecord?.brand_name || null,
        brandId: dbRecord?.brand_id || null,
        dbId: dbRecord?.id || null
      };
    });
  };

  // Handle map number to brand
  const handleMapClick = (number) => {
    setSelectedNumber(number);
    setMapForm({
      brandId: number.brandId || '',
      friendlyName: number.friendlyName || '',
      countryCode: detectCountryCode(number.phoneNumber),
      ringTimeout: number.dbRecord?.ring_timeout || 30,
      voicemailEnabled: number.dbRecord?.voicemail_enabled !== 0,
      callbackEnabled: number.dbRecord?.callback_enabled !== 0
    });
    setError(''); setMapDialogOpen(true);
  };

  // Handle edit mapping
  const handleEditClick = (number) => {
    setSelectedNumber(number);
    setMapForm({
      brandId: number.brandId || '',
      friendlyName: number.dbRecord?.friendly_name || number.friendlyName || '',
      countryCode: number.dbRecord?.country_code || detectCountryCode(number.phoneNumber),
      ringTimeout: number.dbRecord?.ring_timeout || 30,
      voicemailEnabled: number.dbRecord?.voicemail_enabled !== 0,
      callbackEnabled: number.dbRecord?.callback_enabled !== 0
    });
    setError(''); setEditDialogOpen(true);
  };

  // Detect country code from phone number
  const detectCountryCode = (phoneNumber) => {
    if (!phoneNumber) return '';
    const normalized = phoneNumber.replace(/\D/g, '');
    if (normalized.startsWith('1') && normalized.length === 11) return 'US';
    if (normalized.startsWith('44')) return 'GB';
    if (normalized.startsWith('61')) return 'AU';
    if (normalized.startsWith('65')) return 'SG';
    if (normalized.startsWith('60')) return 'MY';
    if (normalized.startsWith('63')) return 'PH';
    return '';
  };

  // Save mapping (create or update)
  const handleSaveMapping = async () => {
    try {
      setError('');

      if (!mapForm.brandId) {
        setError('Please select a brand');
        return;
      }

      const data = {
        brandId: parseInt(mapForm.brandId),
        phoneNumber: selectedNumber.phoneNumber,
        friendlyName: mapForm.friendlyName,
        countryCode: mapForm.countryCode,
        twilioPhoneSid: selectedNumber.sid,
        ringTimeout: mapForm.ringTimeout,
        voicemailEnabled: mapForm.voicemailEnabled,
        callbackEnabled: mapForm.callbackEnabled
      };

      if (selectedNumber.dbId) {
        // Update existing
        await callCentreService.updatePhoneNumber(selectedNumber.dbId, data);
        setSuccess('Phone number mapping updated');
      } else {
        // Create new mapping
        await callCentreService.addPhoneNumber(data);

        // Also configure Twilio webhook if not already configured
        if (!selectedNumber.isConfiguredForCallCentre) {
          try {
            await callCentreService.configureTwilioNumber({
              phoneNumberSid: selectedNumber.sid
            });
            setSuccess('Phone number mapped to brand and Twilio webhook configured');
          } catch (webhookErr) {
            console.error('Webhook config failed (non-fatal):', webhookErr);
            setSuccess('Phone number mapped to brand (webhook config failed - configure manually)');
          }
        } else {
          setSuccess('Phone number mapped to brand');
        }
      }

      setMapDialogOpen(false);
      setEditDialogOpen(false);
      loadData();

    } catch (err) {
      console.error('Error saving mapping:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save mapping');
    }
  };

  // Configure Twilio webhook
  const handleConfigureWebhook = async (number) => {
    try {
      setError('');
      await callCentreService.configureTwilioNumber({
        phoneNumberSid: number.sid
      });
      setSuccess(`Webhook configured for ${number.phoneNumber}`);
      loadData();
    } catch (err) {
      console.error('Error configuring webhook:', err);
      setError(err.response?.data?.message || 'Failed to configure webhook');
    }
  };

  // Delete mapping
  const handleDeleteMapping = async (number) => {
    if (!await confirm({
      title: 'Remove Mapping',
      message: `Remove mapping for ${number.phoneNumber}?`,
      confirmText: 'Remove',
      severity: 'warning'
    })) return;

    try {
      setError('');
      await callCentreService.deletePhoneNumber(number.dbId);
      setSuccess('Mapping removed');
      loadData();
    } catch (err) {
      console.error('Error deleting mapping:', err);
      setError(err.response?.data?.message || 'Failed to remove mapping');
    }
  };

  // Setup Twilio
  const handleSetupTwilio = async () => {
    try {
      setError('');
      const result = await callCentreService.setupTwilio();
      if (result.success) {
        setSuccess('Twilio setup complete! Browser calling enabled.');
        setSetupDialogOpen(false);
        loadData();
      } else {
        setError(result.errors?.join(', ') || 'Setup failed');
      }
    } catch (err) {
      console.error('Error setting up Twilio:', err);
      setError(err.response?.data?.message || 'Failed to setup Twilio');
    }
  };

  const mergedNumbers = getMergedNumbers();
  const mappedCount = mergedNumbers.filter(n => n.isMapped).length;
  const callCentreCount = mergedNumbers.filter(n => n.usage === 'call_centre').length;
  const studioFlowCount = mergedNumbers.filter(n => n.usage === 'studio_flow').length;
  const availableCount = mergedNumbers.filter(n => n.usage === 'available').length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Call Centre Phone Numbers</Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={loadData}
          variant="outlined"
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {/* Alerts */}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Status Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Typography color="text.secondary" variant="body2" gutterBottom>Total</Typography>
              <Typography variant="h5">{twilioNumbers.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Typography color="text.secondary" variant="body2" gutterBottom>Call Centre</Typography>
              <Typography variant="h5" color="success.main">{callCentreCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Typography color="text.secondary" variant="body2" gutterBottom>Studio Flows</Typography>
              <Typography variant="h5" color="info.main">{studioFlowCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Typography color="text.secondary" variant="body2" gutterBottom>Available</Typography>
              <Typography variant="h5" color="warning.main">{availableCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Typography color="text.secondary" variant="body2" gutterBottom>Mapped</Typography>
              <Typography variant="h5">{mappedCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Typography color="text.secondary" variant="body2" gutterBottom>Browser</Typography>
              <Typography variant="h5" color={twilioStatus?.readyForBrowserCalls ? 'success.main' : 'error.main'}>
                {twilioStatus?.readyForBrowserCalls ? 'Ready' : 'No'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Phone Numbers Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Phone Number</TableCell>
              <TableCell>Friendly Name</TableCell>
              <TableCell>Mapped Brand</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Current Usage</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mergedNumbers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No Twilio phone numbers found. Purchase numbers in your Twilio account.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              mergedNumbers.map((number) => (
                <TableRow key={number.sid} hover>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <PhoneIcon fontSize="small" color="action" />
                      <Typography fontFamily="monospace">{number.phoneNumber}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {number.dbRecord?.friendly_name || number.friendlyName || '-'}
                  </TableCell>
                  <TableCell>
                    {number.isMapped ? (
                      <Chip
                        label={number.brandName}
                        color="primary"
                        size="small"
                        icon={<LinkIcon />}
                      />
                    ) : (
                      <Chip
                        label="Unmapped"
                        color="warning"
                        size="small"
                        variant="outlined"
                        icon={<LinkOffIcon />}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {number.dbRecord?.country_code || detectCountryCode(number.phoneNumber) || '-'}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const usage = number.usage || 'unknown';
                      const usageLabel = number.usageLabel || 'Unknown';

                      if (usage === 'call_centre') {
                        return (
                          <Tooltip title="Calls route to Call Centre">
                            <Chip label="Call Centre" color="success" size="small" icon={<CloudDoneIcon />} />
                          </Tooltip>
                        );
                      }
                      if (usage === 'studio_flow') {
                        return (
                          <Tooltip title="Used by Twilio Studio Flow - do not change">
                            <Chip label="Studio Flow" color="info" size="small" />
                          </Tooltip>
                        );
                      }
                      if (usage === 'twiml_bin') {
                        return (
                          <Tooltip title="Used by TwiML Bin">
                            <Chip label="TwiML Bin" color="secondary" size="small" variant="outlined" />
                          </Tooltip>
                        );
                      }
                      if (usage === 'available') {
                        return (
                          <Tooltip title="No webhook configured - available for Call Centre">
                            <Chip label="Available" color="warning" size="small" variant="outlined" />
                          </Tooltip>
                        );
                      }
                      if (usage === 'external') {
                        return (
                          <Tooltip title={`External webhook: ${number.voiceUrl}`}>
                            <Chip label="External" color="default" size="small" variant="outlined" />
                          </Tooltip>
                        );
                      }
                      return (
                        <Tooltip title={number.voiceUrl || 'No webhook'}>
                          <Chip label={usageLabel} color="default" size="small" variant="outlined" />
                        </Tooltip>
                      );
                    })()}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {!number.isMapped ? (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => handleMapClick(number)}
                        >
                          Map
                        </Button>
                      ) : (
                        <>
                          <Tooltip title="Edit Mapping">
                            <IconButton size="small" onClick={() => handleEditClick(number)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove Mapping">
                            <IconButton size="small" color="error" onClick={() => handleDeleteMapping(number)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {/* Only show configure button for available numbers or twiml bins, not studio flows */}
                      {!number.isConfiguredForCallCentre && number.usage !== 'studio_flow' && (
                        <Tooltip title={number.usage === 'available' ? 'Configure for Call Centre' : 'Switch to Call Centre (will override current webhook)'}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleConfigureWebhook(number)}
                          >
                            <SettingsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Map/Edit Dialog */}
      <Dialog
        open={mapDialogOpen || editDialogOpen}
        onClose={() => { setMapDialogOpen(false); setEditDialogOpen(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editDialogOpen ? 'Edit Phone Number Mapping' : 'Map Phone Number to Brand'}
        </DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Phone Number: <strong>{selectedNumber?.phoneNumber}</strong>
            </Typography>

            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Brand *</InputLabel>
              <Select
                value={mapForm.brandId}
                onChange={(e) => setMapForm({ ...mapForm, brandId: e.target.value })}
                label="Brand *"
              >
                {brands.map(brand => (
                  <MenuItem key={brand.id} value={brand.id}>{brand.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Friendly Name"
              value={mapForm.friendlyName}
              onChange={(e) => setMapForm({ ...mapForm, friendlyName: e.target.value })}
              sx={{ mt: 2 }}
              placeholder="e.g., US Support Line"
            />

            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Country</InputLabel>
              <Select
                value={mapForm.countryCode}
                onChange={(e) => setMapForm({ ...mapForm, countryCode: e.target.value })}
                label="Country"
              >
                <MenuItem value="">Auto-detect</MenuItem>
                <MenuItem value="US">United States</MenuItem>
                <MenuItem value="GB">United Kingdom</MenuItem>
                <MenuItem value="AU">Australia</MenuItem>
                <MenuItem value="SG">Singapore</MenuItem>
                <MenuItem value="MY">Malaysia</MenuItem>
                <MenuItem value="PH">Philippines</MenuItem>
              </Select>
            </FormControl>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" gutterBottom>Call Settings</Typography>

            <TextField
              fullWidth
              type="number"
              label="Ring Timeout (seconds)"
              value={mapForm.ringTimeout}
              onChange={(e) => setMapForm({ ...mapForm, ringTimeout: parseInt(e.target.value) || 30 })}
              sx={{ mt: 1 }}
              inputProps={{ min: 10, max: 120 }}
              helperText="How long to ring agents before going to voicemail"
            />

            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={mapForm.voicemailEnabled}
                    onChange={(e) => setMapForm({ ...mapForm, voicemailEnabled: e.target.checked })}
                  />
                }
                label="Enable Voicemail"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={mapForm.callbackEnabled}
                    onChange={(e) => setMapForm({ ...mapForm, callbackEnabled: e.target.checked })}
                  />
                }
                label="Enable Callbacks"
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setMapDialogOpen(false); setEditDialogOpen(false); }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveMapping}>
            {editDialogOpen ? 'Save Changes' : 'Map Number'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Twilio Setup Dialog */}
      <Dialog open={setupDialogOpen} onClose={() => setSetupDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Setup Twilio for Browser Calling</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1 }}>
            This will create an API Key and TwiML Application in your Twilio account.
            These are required for agents to make/receive calls in the browser.
          </Alert>

          {twilioStatus && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Current Status:</Typography>
              <Stack spacing={1}>
                <Typography variant="body2">
                  Account SID: {twilioStatus.status?.hasAccountSid ? <CheckIcon color="success" fontSize="small" /> : <WarningIcon color="warning" fontSize="small" />}
                </Typography>
                <Typography variant="body2">
                  Auth Token: {twilioStatus.status?.hasAuthToken ? <CheckIcon color="success" fontSize="small" /> : <WarningIcon color="warning" fontSize="small" />}
                </Typography>
                <Typography variant="body2">
                  API Key: {twilioStatus.status?.hasApiKey ? <CheckIcon color="success" fontSize="small" /> : <WarningIcon color="warning" fontSize="small" />}
                </Typography>
                <Typography variant="body2">
                  TwiML App: {twilioStatus.status?.hasTwimlApp ? <CheckIcon color="success" fontSize="small" /> : <WarningIcon color="warning" fontSize="small" />}
                </Typography>
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSetupDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSetupTwilio}
            disabled={twilioStatus?.readyForBrowserCalls}
          >
            {twilioStatus?.readyForBrowserCalls ? 'Already Setup' : 'Setup Twilio'}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog {...confirmDialogProps} />
    </Box>
  );
};

export default CallCentrePhoneNumbersTab;
